/**
 * Renderer-side client for local caption extraction and embedding with the bundled
 * cc-embed CLI, which runs in the Electron main process (ccEmbedService.js).
 *
 * Local processing is desktop-only and Pro-only, and needs no connection. Every call
 * rejects with a CcEmbedError carrying the CLI's stable `code` ("no_captions",
 * "unsupported_container", "unsupported", "corrupt_input", "io", "output", "usage",
 * "internal", or "cancelled"); describeCcEmbedError() turns it into text for the user.
 */

import { v4 as uuidv4 } from "uuid";
import * as m from "@app/paraglide/messages.js";
import { environment } from "@app/store/envStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import { can } from "@app/lib/entitlements.js";

const progressHandlers = new Map(); // jobId -> (event) => void
let listenerBound = false;
let versionPromise = null;

export class CcEmbedError extends Error {
    constructor({ code = "internal", exit_code = 1, message = "cc-embed failed", causes = [] } = {}) {
        super(message);
        this.name = "CcEmbedError";
        this.code = code;
        this.exitCode = exit_code;
        this.causes = causes;
    }
}

function bindProgressListener() {
    if (listenerBound || !window.ipcRenderer) {
        return;
    }
    listenerBound = true;
    window.ipcRenderer.on("cc-embed-progress", (event, { jobId, event: progress }) => {
        try {
            progressHandlers.get(jobId)?.(progress);
        } catch (err) {
            console.error("cc-embed progress handler error:", err);
        }
    });
}

async function invoke(channel, options) {
    if (!window.ipcRenderer) {
        throw new CcEmbedError({ code: "unavailable", message: "Local processing is only available in the desktop application" });
    }
    const reply = await window.ipcRenderer.invoke(channel, options);
    if (!reply || reply.error) {
        throw new CcEmbedError(reply?.error);
    }
    return reply.result;
}

/** A new job id, for extract()/embed() and cancel(). */
export function newCcEmbedJobId() {
    return uuidv4();
}

/** The bundled cc-embed's version ("cc-embed 0.1.0"), or null when it isn't there. */
export function getCcEmbedVersion() {
    if (!environment.electron || !window.ipcRenderer) {
        return Promise.resolve(null);
    }
    if (!versionPromise) {
        versionPromise = window.ipcRenderer
            .invoke("cc-embed-version")
            .then((reply) => reply?.result || null)
            .catch(() => null);
    }
    return versionPromise;
}

/** Whether the local options can be offered: desktop app, Pro plan. */
export function canUseLocalCcEmbed() {
    return Boolean(environment.electron && can("embed608"));
}

/** Whether the local options work right now (the binary answers too). */
export async function isLocalCcEmbedAvailable() {
    return canUseLocalCcEmbed() && Boolean(await getCcEmbedVersion());
}

/** `cc-embed probe`: container, streams, start timecode and caption carriage. */
export function probeMedia(input) {
    return invoke("cc-embed-probe", { input });
}

async function runJob(channel, options, onProgress) {
    bindProgressListener();
    const jobId = options.jobId || newCcEmbedJobId();
    if (onProgress) {
        progressHandlers.set(jobId, onProgress);
    }
    try {
        return await invoke(channel, { ...options, jobId });
    } finally {
        // Progress is sent before the reply, so nothing for this job arrives after it.
        progressHandlers.delete(jobId);
    }
}

/**
 * Extract a video's captions: 608/708 as MCC text, or OP-47 as EBU STL bytes.
 *
 * @param {object} opts
 * @param {string} opts.input          absolute path of the video
 * @param {string} [opts.jobId]        for cancelCcEmbedJob()
 * @param {string} [opts.startTc]      overrides the file's own start timecode
 * @param {string} [opts.teletextPage] an OP-47 page such as "801"
 * @param {(event: object) => void} [onProgress]  cc-embed progress events
 * @returns {Promise<{summary: object, data: string|Uint8Array}>}
 *   summary.format is "mcc" or "stl"; summary.salvaged means the file was damaged and
 *   the captions may stop early.
 */
export function extractCaptions(opts, onProgress) {
    return runJob("cc-embed-extract", opts, onProgress);
}

/**
 * Embed captions into a copy of a video, without re-encoding it.
 *
 * @param {object} opts
 * @param {string} opts.input          the video
 * @param {string} opts.output         the new file (must not be the input)
 * @param {string|Uint8Array} opts.captionsData  MCC text, or EBU STL bytes for OP-47 (MXF)
 * @param {string} opts.captionsName   "captions.mcc" or "captions.stl"
 * @param {string} [opts.jobId]
 * @param {boolean} [opts.replace]     replace captions already in the file
 * @param {"track"|"sei"} [opts.carriage]  MOV/MP4: a caption track or A/53 SEI
 * @param {string} [opts.startTc]      the timecode of the first video frame
 * @param {string} [opts.teletextPage] OP-47 page, default "801"
 * @param {boolean} [opts.verify]      re-read the output and compare it with the input
 * @param {(event: object) => void} [onProgress]  events with phase "scan", "write", "verify"
 * @returns {Promise<object>} the embed summary
 */
export function embedCaptions(opts, onProgress) {
    return runJob("cc-embed-embed", opts, onProgress);
}

/** Stop a running extract or embed; it rejects with code "cancelled". */
export async function cancelCcEmbedJob(jobId) {
    if (!window.ipcRenderer) {
        return false;
    }
    const reply = await window.ipcRenderer.invoke("cc-embed-cancel", { jobId });
    return Boolean(reply?.result);
}

/** User-facing text for a failed local job. */
export function describeCcEmbedError(err) {
    const detail = err?.causes?.length ? err.causes[err.causes.length - 1] : err?.message || String(err);
    switch (err?.code) {
        case "cancelled":
            return m.cc_embed_error_cancelled();
        case "unavailable":
            return m.cc_embed_error_unavailable();
        case "no_captions":
            return m.cc_embed_error_no_captions();
        case "unsupported_container":
            return m.cc_embed_error_unsupported_container();
        case "unsupported":
            return m.cc_embed_error_unsupported({ detail });
        case "corrupt_input":
            return m.cc_embed_error_corrupt_input({ detail });
        case "io":
            return m.cc_embed_error_io({ detail });
        case "output":
            return m.cc_embed_error_output({ detail });
        case "usage":
            return m.cc_embed_error_usage({ detail });
        default:
            return m.cc_embed_error_internal({ detail });
    }
}
